/**
 * Figure rims (lane DUEL-ARMOR): a feature may outline a figure in a colour of its own, drawn under the figure as a hard
 * 1-px rim with a 1-px dark ring outside it (pixel rules: no glow, no soft pulse). The renderer asks once per figure
 * (`drawFigureRim`) before its own charging rim; the first provider that answers wins, and the charging rim is skipped.
 * A provider registers itself from its own file (e.g. render/passes/duelArmorTell.ts: ตัวแดง / ตัวเหลือง).
 */
import type { Entity, World } from '@nine/shared';

/** The rim's colour and its dark outer ring (css), or null: none for this figure now. */
export type FigureRimProvider = (
  w: World,
  e: Entity,
) => { readonly rim: string; readonly outer: string; readonly stipple?: boolean } | null;

const PROVIDERS: FigureRimProvider[] = [];

export function registerFigureRim(p: FigureRimProvider): void {
  PROVIDERS.push(p);
}

/** Dark ring first (2 px out and the diagonals), then the rim (1 px out); true when a rim was drawn. */
export function drawFigureRim(
  w: World,
  e: Entity,
  blit: (color: string, dx: number, dy: number) => void,
): boolean {
  if (e.dead) return false;
  for (const p of PROVIDERS) {
    const r = p(w, e);
    if (!r) continue;
    for (const [dx, dy] of OUTER) blit(r.outer, dx, dy);
    if (r.stipple) {
      // Stippled rim (ตัวแดง): dark under it, then the rim on every 2nd pixel; diagonal shifts keep the checker phase.
      for (const [dx, dy] of INNER) blit(r.outer, dx, dy);
      const pat = checker(r.rim);
      if (pat) for (const [dx, dy] of DIAG) blit(pat, dx, dy);
    } else for (const [dx, dy] of INNER) blit(r.rim, dx, dy);
    return true;
  }
  return false;
}

const INNER: readonly (readonly [number, number])[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];
const OUTER: readonly (readonly [number, number])[] = [
  [-2, 0],
  [2, 0],
  [0, -2],
  [0, 2],
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
];

const DIAG: readonly (readonly [number, number])[] = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
];

const CHECKERS = new Map<string, string>();
/**
 * A 2x2 checker fill of one colour. The renderer's silhouette cache takes it as a colour (source-in fill accepts a
 * CanvasPattern; Map keys by identity), so the stipple costs nothing per frame. Typed string for that hand-off only.
 */
function checker(color: string): string | null {
  const hit = CHECKERS.get(color) as unknown;
  if (hit) return hit as string;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = c.height = 2;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = color;
  g.fillRect(0, 0, 1, 1);
  g.fillRect(1, 1, 1, 1);
  const pat = g.createPattern(c, 'repeat');
  if (!pat) return null;
  CHECKERS.set(color, pat as unknown as string);
  return pat as unknown as string;
}
